import {
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import type {
  AccessToken,
  CreateTokenParams,
  UpdateTokenParams,
  ValidateResponse,
} from "../server/services/access";

import { useDelete, useGet, usePatch, usePost } from "./http";

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

const TOKENS_KEY = "/api/access/tokens";

export function useAccessTokens(
  options?: Omit<UseQueryOptions<AccessToken[], Error>, "queryKey" | "queryFn">
) {
  return useGet<AccessToken[]>(TOKENS_KEY, options);
}

export function useCreateAccessToken(
  options?: UseMutationOptions<AccessToken, Error, CreateTokenParams>
) {
  const queryClient = useQueryClient();
  return usePost<CreateTokenParams, AccessToken>(TOKENS_KEY, {
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [TOKENS_KEY] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateAccessToken(
  options?: UseMutationOptions<
    AccessToken,
    Error,
    { id: string } & UpdateTokenParams
  >
) {
  const queryClient = useQueryClient();
  return usePatch<{ id: string } & UpdateTokenParams, AccessToken>(
    ({ id, ...body }) => ({ url: `${TOKENS_KEY}/${id}`, body }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries({ queryKey: [TOKENS_KEY] });
        options?.onSuccess?.(...args);
      },
    }
  );
}

export function useDeleteAccessToken(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();
  return useDelete<string>((id) => `${TOKENS_KEY}/${id}`, {
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [TOKENS_KEY] });
      options?.onSuccess?.(...args);
    },
  });
}

export function useValidateAccess(
  options?: UseMutationOptions<
    ValidateResponse,
    Error,
    { token?: string; path: string }
  >
) {
  return usePost<{ token?: string; path: string }, ValidateResponse>(
    "/api/access/validate",
    options
  );
}
